import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    // ✅ 移除不必要的 setTimeout(50ms)，直接初始化
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentUser(session?.user ?? null);
      setAuthReady(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      switch (event) {
        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED':
        case 'USER_UPDATED':
          setCurrentUser(session?.user ?? null);
          setAuthReady(true);
          break;

        case 'PASSWORD_RECOVERY':
          sessionStorage.setItem('password_recovery_mode', 'true');
          setCurrentUser(session?.user ?? null);
          setAuthReady(true);
          setTimeout(() => {
            const recoveryLang = localStorage.getItem('language') || 'en';
            window.location.hash = `#/${recoveryLang}/reset-password`;
          }, 100);
          break;

        case 'SIGNED_OUT':
          setCurrentUser(null);
          setIsLoggingOut(false);
          setAuthReady(true);
          localStorage.removeItem('supabase.auth.token');
          sessionStorage.clear();
          setTimeout(() => {
            const currentLanguage = localStorage.getItem('language') || 'en';
            window.location.hash = `#/${currentLanguage}`;
          }, 100);
          break;

        case 'INITIAL_SESSION':
          setCurrentUser(session?.user ?? null);
          setAuthReady(true);
          break;

        default:
          break;
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signUp = useCallback(async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      if (data.user) setCurrentUser(data.user);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  const signIn = useCallback(async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data.user) setCurrentUser(data.user);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  const signOut = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      setIsLoggingOut(false);
      return { error };
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      const currentLanguage = localStorage.getItem('language') || 'en';
      localStorage.setItem('language', currentLanguage);
      const isProduction = window.location.hostname === 'petcarenote.com';
      const redirectUrl = isProduction ? 'https://petcarenote.com' : window.location.origin;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  // ✅ 別名直接指向同一函數引用，不重複定義
  const value = {
    currentUser,
    user: currentUser,
    loading: false,
    isLoggingOut,
    authReady,
    signUp,
    signIn,
    login: signIn,
    signOut,
    logout: signOut,
    signInWithGoogle,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};